import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { formatMoney, thumbLetter, useCatalogMap } from './catalog';
import { useComboPreviewImages } from './combo';
import { IconCart, IconClose, IconSearch, IconVoucher } from './icons';
import { JudgeMeSummary } from './JudgeMeReviews';
import { livePriceDisplay, ProductQuote, quoteProductCheckout } from './pricing';
import { useSwipeClose } from './SwipeSheet';
import {
  VariantPicker,
  VariantPickerTarget,
  maybeOpenVariantPicker,
} from './VariantPicker';
import { VoucherChips } from './VoucherChips';
import {
  LiveProduct,
  LiveSessionPublic,
  comboDiscountId,
  resolvePinnedProductId,
  sameProductId,
} from './utils';

interface Props {
  apiUrl: string;
  session: LiveSessionPublic;
  productViews: Record<string, number>;
  onClose: () => void;
  onSelectProduct: (p: LiveProduct) => void;
  onAddToCart: (productId: string, variantId?: string) => Promise<void>;
  onBuyNow: (productId: string, variantId?: string) => Promise<void>;
  cartCount: number;
  cartBump?: boolean;
  onOpenCart: () => void;
}

export function ProductBagSheet({
  apiUrl,
  session,
  onClose,
  onSelectProduct,
  onAddToCart,
  onBuyNow,
  cartCount,
  cartBump = false,
  onOpenCart,
}: Props) {
  const catalog = useCatalogMap(session.products);
  const swipeClose = useSwipeClose(onClose);
  const products = session.products || [];
  const comboImages = useComboPreviewImages(apiUrl, products);
  const discounts = session.discounts || [];
  const [query, setQuery] = useState('');
  const [quotes, setQuotes] = useState<Record<string, ProductQuote>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [variantTarget, setVariantTarget] = useState<VariantPickerTarget | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        products.map(async (p) => {
          const q = await quoteProductCheckout(apiUrl, p, undefined, session.id);
          return [p.item_id, q] as const;
        })
      );
      if (cancelled) return;
      const next: Record<string, ProductQuote> = {};
      for (const [id, q] of entries) {
        if (q) next[id] = q;
      }
      setQuotes(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, session.id, products.map((p) => p.item_id).join(',')]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pinnedId = resolvePinnedProductId(session);
    let list = products;
    if (q) {
      list = products.filter((p) => {
        const info = catalog.get(String(p.item_id));
        const name = (info?.title || p.item_name || p.item_id).toLowerCase();
        return name.includes(q);
      });
    }
    // Keep natural order, and also surface the pinned product in a top slot.
    if (!pinnedId || q) return list.map((p) => ({ product: p, key: p.item_id }));
    const pinned = list.find((p) => sameProductId(p.item_id, pinnedId));
    if (!pinned) return list.map((p) => ({ product: p, key: p.item_id }));
    return [
      { product: pinned, key: `pin-${pinned.item_id}` },
      ...list.map((p) => ({ product: p, key: p.item_id })),
    ];
  }, [products, query, catalog, session.showcased_product_id, session.products]);

  return (
    <div className="il-sheet il-sheet-tall" role="dialog">
      <div className="il-sheet-grab" aria-hidden>
        <div className="il-sheet-handle" />
      </div>
      <div className="il-bag-toolbar">
        <div className="il-bag-search">
          <IconSearch />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products"
            aria-label="Search products"
          />
        </div>
        <button
          type="button"
          className={`il-bag-cart${cartBump ? ' is-bump' : ''}`}
          onClick={onOpenCart}
          aria-label={cartCount > 0 ? `Cart, ${cartCount} items` : 'Cart'}
        >
          <IconCart size={18} />
          {cartCount > 0 && <span className="il-badge il-badge-pink">{cartCount}</span>}
        </button>
        <button type="button" className="il-sheet-x il-sheet-x-rel" onClick={swipeClose} aria-label="Close">
          <IconClose size={18} />
        </button>
      </div>

      <VoucherChips
        discounts={discounts}
        products={products}
        onOpenProduct={onSelectProduct}
      />

      <div className="il-bag-section-label">
        Shop LIVE · {(query.trim() ? filtered.length : products.length)} products
      </div>

      {filtered.length === 0 ? (
        <p className="il-empty">No products found</p>
      ) : (
        <ul className="il-bag-list">
          {filtered.map(({ product: p, key }) => {
            const info = catalog.get(String(p.item_id));
            const image = info?.image || comboImages.get(String(p.item_id)) || null;
            const title = info?.title || p.item_name || p.item_id;
            const quote = quotes[p.item_id];
            const isCombo = Boolean(comboDiscountId(p));
            const pinned = sameProductId(resolvePinnedProductId(session), p.item_id);
            const label = (Number(p.sort_order) || 0) + 1;
            const busyAdd = busyKey === `${p.item_id}:add`;
            const busyBuy = busyKey === `${p.item_id}:buy`;
            const busy = busyAdd || busyBuy;

            const finalPrice =
              quote?.finalTotal != null
                ? quote.finalTotal
                : info?.price != null
                  ? Number(info.price)
                  : null;
            const compareAt =
              quote?.compareAt != null
                ? quote.compareAt
                : info?.compareAt != null
                  ? Number(info.compareAt)
                  : quote && quote.originalTotal > quote.finalTotal
                    ? quote.originalTotal
                    : null;
            const strikes = livePriceDisplay(finalPrice, quote, compareAt);
            const voucherDiscount =
              quote && quote.voucherDiscount > 0 ? quote.voucherDiscount : null;
            const slashForPct = strikes.compare ?? strikes.preLive;
            const pctOff =
              slashForPct != null && strikes.now != null && slashForPct > strikes.now
                ? Math.round(((slashForPct - strikes.now) / slashForPct) * 100)
                : null;

            const runAdd = async (e: MouseEvent) => {
              e.stopPropagation();
              if (busy) return;
              // Combos need the multi-step picker in the PDP
              if (isCombo) {
                onSelectProduct(p);
                return;
              }
              if (maybeOpenVariantPicker(info, p, 'add', setVariantTarget)) return;
              try {
                setBusyKey(`${p.item_id}:add`);
                await onAddToCart(p.item_id);
              } finally {
                setBusyKey(null);
              }
            };

            const runBuy = async (e: MouseEvent) => {
              e.stopPropagation();
              if (busy) return;
              if (isCombo) {
                onSelectProduct(p);
                return;
              }
              if (maybeOpenVariantPicker(info, p, 'buy', setVariantTarget)) return;
              try {
                setBusyKey(`${p.item_id}:buy`);
                await onBuyNow(p.item_id);
              } finally {
                setBusyKey(null);
              }
            };

            return (
              <li key={key}>
                <div
                  className="il-bag-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectProduct(p)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectProduct(p);
                    }
                  }}
                >
                  <span className="il-bag-thumb">
                    <span className="il-bag-num" aria-hidden>
                      {label}
                    </span>
                    {image ? (
                      <img src={image} alt="" />
                    ) : (
                      <span className="il-bag-ph">{thumbLetter(title)}</span>
                    )}
                    {pinned && (
                      <span className="il-pin-tag">
                        <span className="il-pin-tag-dot" aria-hidden />
                        LIVE
                      </span>
                    )}
                  </span>
                  <span className="il-bag-meta">
                    <span className="il-bag-name">{title}</span>
                    {!isCombo && <JudgeMeSummary apiUrl={apiUrl} productId={p.item_id} />}
                    {quote?.freeShipping && (
                      <span className="il-bag-stats">
                        <span className="il-stat-ship">Free shipping</span>
                      </span>
                    )}
                    <span className="il-bag-footer">
                      <span className="il-bag-prices">
                        {(strikes.preLive != null || strikes.compare != null) && (
                          <span className="il-bag-price-top">
                            {strikes.preLive != null && (
                              <span className="il-price-was-live">
                                RM {formatMoney(strikes.preLive)}
                              </span>
                            )}
                            {strikes.compare != null && (
                              <span className="il-price-was-compare">
                                RM {formatMoney(strikes.compare)}
                              </span>
                            )}
                            {pctOff != null && pctOff > 0 && (
                              <span className="il-bag-pct">{pctOff}% off</span>
                            )}
                          </span>
                        )}
                        <span className="il-bag-price-now">
                          <span className="il-price">
                            {strikes.now != null ? `RM ${formatMoney(strikes.now)}` : 'View'}
                          </span>
                          {voucherDiscount != null && (
                            <span
                              className="il-price-voucher il-bag-voucher-icon"
                              title={`Voucher −RM ${formatMoney(voucherDiscount)}`}
                            >
                              <IconVoucher size={16} />
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="il-bag-actions">
                        <button
                          type="button"
                          className="il-bag-atc"
                          aria-label="Add to cart"
                          disabled={busy}
                          onClick={runAdd}
                        >
                          {busyAdd ? (
                            <span className="il-btn-spinner il-btn-spinner-dark" aria-hidden />
                          ) : (
                            <IconCart size={16} />
                          )}
                        </button>
                        <button
                          type="button"
                          className="il-bag-cta"
                          disabled={busy}
                          onClick={runBuy}
                        >
                          {busyBuy ? <span className="il-btn-spinner" aria-hidden /> : 'Buy'}
                        </button>
                      </span>
                    </span>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <VariantPicker
        target={variantTarget}
        onClose={() => setVariantTarget(null)}
        onConfirm={async (productId, variantId, mode) => {
          if (mode === 'buy') await onBuyNow(productId, variantId);
          else await onAddToCart(productId, variantId);
        }}
      />
    </div>
  );
}
